import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProvider } from '../../contexts/ProviderContext';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../lib/supabase';
import {
  updateOrderStatus,
  formatCurrency,
  formatDate,
  getStatusColor,
  ProviderOrder,
} from '../../lib/providerOrders';
import {
  TruckIcon,
  MapPinIcon,
  UserIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import ProviderLayout from './ProviderLayout';

const ProviderOrderDetails: React.FC = () => {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const { currentProviderId } = useProvider();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState<ProviderOrder | null>(null);

  // Update form state
  const [fulfillmentStatus, setFulfillmentStatus] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [carrier, setCarrier] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (orderId && currentProviderId) {
      loadOrderDetails();
    }
  }, [orderId, currentProviderId]);

  const loadOrderDetails = async () => {
    if (!orderId || !currentProviderId) return;

    try {
      setLoading(true);

      // Fetch order details from provider_orders_view
      const { data, error: fetchError } = await supabase
        .from('provider_orders_view')
        .select('*')
        .eq('order_id', orderId)
        .eq('provider_id', currentProviderId)
        .single();

      if (fetchError) throw fetchError;
      if (!data) throw new Error('Order not found');

      setOrder(data as ProviderOrder);

      // Initialize form with existing data
      if (data.latest_provider_update) {
        setFulfillmentStatus(data.latest_provider_update.status || data.display_fulfillment_status || 'pending');
        setTrackingNumber(data.latest_provider_update.tracking_number || '');
        setTrackingUrl(data.latest_provider_update.tracking_url || '');
        setCarrier(data.latest_provider_update.carrier || '');
        setNotes(data.latest_provider_update.notes || '');
      } else {
        setFulfillmentStatus(data.display_fulfillment_status || 'pending');
      }
    } catch (err) {
      console.error('Error loading order:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderId || !currentProviderId) return;

    try {
      setSaving(true);

      await updateOrderStatus({
        provider_id: currentProviderId,
        order_id: orderId,
        fulfillment_status: fulfillmentStatus,
        tracking_number: trackingNumber || undefined,
        tracking_url: trackingUrl || undefined,
        carrier: carrier || undefined,
        notes: notes || undefined,
      });

      toast.success('Order status updated successfully!');

      // Reload order details to show updated data
      await loadOrderDetails();
    } catch (err) {
      console.error('Error updating order status:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update order status');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadgeClass = () => {
    const color = getStatusColor(fulfillmentStatus);
    const colorClasses: Record<string, string> = {
      emerald: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
      yellow: 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20',
      blue: 'bg-blue-500/10 text-blue-400 border border-blue-500/20',
      indigo: 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20',
      purple: 'bg-purple-500/10 text-purple-400 border border-purple-500/20',
      green: 'bg-green-500/10 text-green-400 border border-green-500/20',
      red: 'bg-red-500/10 text-red-400 border border-red-500/20',
      orange: 'bg-orange-500/10 text-orange-400 border border-orange-500/20',
      gray: 'bg-white/[0.06] text-white/50 border border-white/15',
    };
    return colorClasses[color] || colorClasses.gray;
  };

  if (loading) {
    return (
      <ProviderLayout backTo="/provider/orders">
        <div className="max-w-7xl mx-auto">
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-4 text-white/50 ">Loading order details...</p>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  if (!order) return null;

  return (
    <ProviderLayout backTo="/provider/orders">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white ">
                ORDER {order.order_number}
              </h1>
              <p className="text-white/50 mt-2">
                Placed on {formatDate(order.created_at)}
              </p>
            </div>
            <div className={`px-4 py-2 rounded-full ${getStatusBadgeClass()}  font-semibold text-sm`}>
              {fulfillmentStatus.replace(/_/g, ' ').toUpperCase()}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Line Items */}
            <div className=" rounded-lg shadow border border-white/[0.08]">
              <div className="p-6 border-b border-white/[0.08]">
                <h2 className="text-xl font-bold text-white  flex items-center gap-2">
                  <ShoppingBagIcon className="w-6 h-6" />
                  ORDER ITEMS
                </h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {order.line_items.map((item: any, index: number) => (
                    <div
                      key={index}
                      className="flex items-start gap-4 p-4 bg-transparent rounded border border-white/[0.08]"
                    >
                      <div className="flex-1">
                        <h3 className="font-semibold text-white">{item.name || item.title}</h3>
                        <div className="mt-2 space-y-1 text-sm text-white/50 ">
                          {item.sku && <p>SKU: {item.sku}</p>}
                          {item.variant_title && <p>Variant: {item.variant_title}</p>}
                          <p>Quantity: {item.quantity}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-white ">
                          {formatCurrency(parseFloat(item.price) * parseInt(item.quantity), order.currency_code)}
                        </p>
                        <p className="text-sm text-white/50 ">
                          {formatCurrency(parseFloat(item.price), order.currency_code)} each
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-white/[0.08] space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50 ">Subtotal:</span>
                    <span className="font-semibold ">
                      {formatCurrency(order.current_subtotal_price_amount, order.currency_code)}
                    </span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-white/[0.08]">
                    <span className="">TOTAL:</span>
                    <span className="">
                      {formatCurrency(order.current_total_price_amount, order.currency_code)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer & Shipping Info */}
            <div className=" rounded-lg shadow border border-white/[0.08]">
              <div className="p-6 border-b border-white/[0.08]">
                <h2 className="text-xl font-bold text-white  flex items-center gap-2">
                  <UserIcon className="w-6 h-6" />
                  CUSTOMER & SHIPPING
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Customer Info */}
                  <div>
                    <h3 className="font-semibold text-white mb-3 ">CUSTOMER</h3>
                    <div className="space-y-2 text-sm">
                      {order.customer_email && <p>{order.customer_email}</p>}
                      {order.customer_phone && <p>{order.customer_phone}</p>}
                    </div>
                  </div>

                  {/* Shipping Address */}
                  <div>
                    <h3 className="font-semibold text-white mb-3  flex items-center gap-2">
                      <MapPinIcon className="w-5 h-5" />
                      SHIPPING ADDRESS
                    </h3>
                    {order.shipping_address ? (
                      <div className="space-y-1 text-sm">
                        {order.shipping_address.name && <p className="font-semibold">{order.shipping_address.name}</p>}
                        {order.shipping_address.address1 && <p>{order.shipping_address.address1}</p>}
                        {order.shipping_address.address2 && <p>{order.shipping_address.address2}</p>}
                        <p>
                          {order.shipping_address.city && `${order.shipping_address.city}, `}
                          {order.shipping_address.province_code && `${order.shipping_address.province_code} `}
                          {order.shipping_address.zip}
                        </p>
                        {order.shipping_address.country && <p>{order.shipping_address.country}</p>}
                      </div>
                    ) : (
                      <p className="text-white/35 text-sm">No shipping address provided</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - 1 column */}
          <div className="space-y-6">
            {/* Merchant Info */}
            <div className=" rounded-lg shadow border border-white/[0.08] p-6">
              <h3 className="font-semibold text-white mb-3 ">MERCHANT</h3>
              <div className="space-y-2 text-sm">
                <p className="font-semibold">{order.merchant_shop_name}</p>
                <p className="text-white/50">{order.merchant_shop_domain}</p>
                <p className="text-white/50">{order.merchant_email}</p>
              </div>
            </div>

            {/* Update Status Form */}
            <div className=" rounded-lg shadow border border-white/[0.08] p-6">
              <h3 className="font-semibold text-white mb-4  flex items-center gap-2">
                <TruckIcon className="w-5 h-5" />
                UPDATE FULFILLMENT
              </h3>
              <form onSubmit={handleUpdateStatus} className="space-y-4">
                <div>
                  <label className="block text-sm  font-semibold text-white/70 mb-2">
                    STATUS *
                  </label>
                  <select
                    value={fulfillmentStatus}
                    onChange={(e) => setFulfillmentStatus(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-white/15 rounded  text-white  text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="order_placed">Order Placed</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="in_transit">In Transit</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm  font-semibold text-white/70 mb-2">
                    TRACKING NUMBER
                  </label>
                  <input
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-white/15 rounded  text-white  text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter tracking number"
                  />
                </div>

                <div>
                  <label className="block text-sm  font-semibold text-white/70 mb-2">
                    TRACKING URL
                  </label>
                  <input
                    type="url"
                    value={trackingUrl}
                    onChange={(e) => setTrackingUrl(e.target.value)}
                    className="w-full px-3 py-2 border border-white/15 rounded  text-white  text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label className="block text-sm  font-semibold text-white/70 mb-2">
                    CARRIER
                  </label>
                  <input
                    type="text"
                    value={carrier}
                    onChange={(e) => setCarrier(e.target.value)}
                    className="w-full px-3 py-2 border border-white/15 rounded  text-white  text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., UPS, FedEx, DHL"
                  />
                </div>

                <div>
                  <label className="block text-sm  font-semibold text-white/70 mb-2">
                    NOTES
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-white/15 rounded  text-white  text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Add any notes about this update..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  className="w-full px-4 py-3 bg-black text-white rounded  font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'UPDATING...' : 'UPDATE STATUS'}
                </button>
              </form>
            </div>

            {/* Order Info */}
            <div className=" rounded-lg shadow border border-white/[0.08] p-6">
              <h3 className="font-semibold text-white mb-3 ">ORDER INFO</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-white/50 ">Order ID:</span>
                  <p className="">{order.order_id}</p>
                </div>
                <div>
                  <span className="text-white/50 ">Shopify ID:</span>
                  <p className="">{order.shopify_order_id}</p>
                </div>
                {order.external_order_id && (
                  <div>
                    <span className="text-white/50 ">External ID:</span>
                    <p className="">{order.external_order_id}</p>
                  </div>
                )}
                <div>
                  <span className="text-white/50 ">Financial Status:</span>
                  <p className="">{order.display_financial_status || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProviderLayout>
  );
};

export default ProviderOrderDetails;
